import { COLORS } from '../constants/theme'

export const SLOT_MINUTES = 15
export const PENDING_STRIPE_COLOR = '#F5A623'

export function formatSlotTime(iso: string): string {
    const match = iso.match(/T(\d{2}):(\d{2})/)
    if (match) return `${match[1]}:${match[2]}`
    const d = new Date(iso)
    if (!Number.isNaN(d.getTime())) {
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
    }
    return '--:--'
}

/** Minutes since midnight from a local ISO string `YYYY-MM-DDTHH:mm:00`. */
export function minutesOfDay(iso: string): number {
    const match = iso.match(/T(\d{2}):(\d{2})/)
    if (!match) return 0
    return Number(match[1]) * 60 + Number(match[2])
}

/** Build local ISO time on a date from minutes since midnight. */
export function isoAtMinutes(date: string, minutesSinceMidnight: number): string {
    const pad = (n: number) => String(n).padStart(2, '0')
    const h = Math.floor(minutesSinceMidnight / 60)
    const m = minutesSinceMidnight % 60
    return `${date}T${pad(h)}:${pad(m)}:00`
}

/** Snap click ratio on the day timeline to a slot boundary (timezone-safe). */
export function timeAtDayRatio(
    date: string,
    clubOpen: string,
    clubClose: string,
    ratio: number,
    slotMinutes = SLOT_MINUTES
): string {
    const openMin = minutesOfDay(`${date}T${clubOpen}:00`)
    const closeMin = minutesOfDay(`${date}T${clubClose}:00`)
    const total = closeMin - openMin
    if (total <= 0) return isoAtMinutes(date, openMin)
    const raw = Math.max(0, Math.min(total, ratio * total))
    const snapped = Math.floor(raw / slotMinutes) * slotMinutes
    return isoAtMinutes(date, openMin + snapped)
}

export function formatSlotDate(iso: string): string {
    const d = new Date(iso)
    return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })
}

export function formatDayHeader(dateStr: string, today: boolean): string {
    const d = new Date(dateStr + 'T12:00:00')
    const label = d.toLocaleDateString([], { weekday: 'short', day: 'numeric' })
    return today ? `${label} · Today` : label
}

export function todayIsoDate(): string {
    const d = new Date()
    return d.toISOString().slice(0, 10)
}

export function heatmapBackground(ratio: number): string {
    const clamped = Math.min(1, Math.max(0, ratio))
    if (clamped <= 0) return COLORS.white
    // White → red via alpha (readable at ~9% occupancy for 1/11 tables)
    const alpha = 0.06 + clamped * 0.94
    return `rgba(220, 38, 38, ${alpha.toFixed(3)})`
}

/** Prefer API ratio; fall back to occupied / total when missing or invalid. */
export function resolveOccupancyRatio(
    occupancyRatio: number | undefined | null,
    occupiedCount: number,
    totalTables: number
): number {
    if (typeof occupancyRatio === 'number' && Number.isFinite(occupancyRatio)) {
        return Math.min(1, Math.max(0, occupancyRatio))
    }
    if (totalTables <= 0) return 0
    return Math.min(1, Math.max(0, occupiedCount / totalTables))
}

export function snapToSlotMinutes(totalMinutes: number): number {
    return Math.max(SLOT_MINUTES, Math.round(totalMinutes / SLOT_MINUTES) * SLOT_MINUTES)
}

export function addMinutesToIso(iso: string, minutes: number): string {
    const d = new Date(iso)
    d.setMinutes(d.getMinutes() + minutes)
    return toLocalIso(d)
}

export function toLocalIso(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`
}

export function parseIso(iso: string): Date {
    return new Date(iso)
}

export function minutesBetween(startIso: string, endIso: string): number {
    return (parseIso(endIso).getTime() - parseIso(startIso).getTime()) / 60000
}

export function buildHourLabels(clubOpen: string, clubClose: string): string[] {
    const [openH] = clubOpen.split(':').map(Number)
    const [closeH] = clubClose.split(':').map(Number)
    const labels: string[] = []
    for (let h = openH; h <= closeH; h++) {
        labels.push(String(h).padStart(2, '0'))
    }
    return labels
}

export function slotCount(clubOpen: string, clubClose: string, slotMinutes = SLOT_MINUTES): number {
    const [openH, openM] = clubOpen.split(':').map(Number)
    const [closeH, closeM] = clubClose.split(':').map(Number)
    const total = (closeH * 60 + closeM) - (openH * 60 + openM)
    return Math.floor(total / slotMinutes)
}

export interface NextAvailableSlot {
    startTime: string
    date: string
    freeCount: number
}

export function findNextAvailableSlots(
    days: { date: string; slots: { startTime: string; freeTableIds: number[] }[] }[],
    limit = 5,
    afterIso?: string
): NextAvailableSlot[] {
    const after = afterIso ? parseIso(afterIso).getTime() : Date.now()
    const result: NextAvailableSlot[] = []
    for (const day of days) {
        for (const slot of day.slots) {
            if (parseIso(slot.startTime).getTime() < after) continue
            if (slot.freeTableIds.length === 0) continue
            result.push({
                startTime: slot.startTime,
                date: day.date,
                freeCount: slot.freeTableIds.length,
            })
            if (result.length >= limit) return result
        }
    }
    return result
}

export function slotIndexFromPointer(
    clientY: number,
    gridTop: number,
    rowHeight: number,
    maxSlots: number
): number {
    const idx = Math.floor((clientY - gridTop) / rowHeight)
    return Math.max(0, Math.min(maxSlots - 1, idx))
}

export const QUARTER_MINUTES = [0, 15, 30, 45] as const

export function parseSlotIso(iso: string): { hour: number; minute: number } {
    const match = iso.match(/T(\d{2}):(\d{2})/)
    return { hour: Number(match?.[1] ?? 0), minute: Number(match?.[2] ?? 0) }
}

export function composeSlotIso(date: string, hour: number, minute: number): string {
    return isoAtMinutes(date, hour * 60 + minute)
}

export function clubDayBounds(date: string, clubOpen: string, clubClose: string): {
    openMin: number
    closeMin: number
} {
    return {
        openMin: minutesOfDay(`${date}T${clubOpen}:00`),
        closeMin: minutesOfDay(`${date}T${clubClose}:00`),
    }
}

/** All valid start slots within club hours (room for at least one 15-min slot before close). */
export function buildStartSlotIsos(date: string, clubOpen: string, clubClose: string): string[] {
    const { openMin, closeMin } = clubDayBounds(date, clubOpen, clubClose)
    const maxStart = closeMin - SLOT_MINUTES
    const slots: string[] = []
    for (let m = openMin; m <= maxStart; m += SLOT_MINUTES) {
        slots.push(isoAtMinutes(date, m))
    }
    return slots
}

/** Valid end slots strictly after start, up to club close. */
export function buildEndSlotIsos(
    date: string,
    clubOpen: string,
    clubClose: string,
    startIso: string
): string[] {
    const { closeMin } = clubDayBounds(date, clubOpen, clubClose)
    const minEnd = minutesOfDay(startIso) + SLOT_MINUTES
    const slots: string[] = []
    for (let m = minEnd; m <= closeMin; m += SLOT_MINUTES) {
        slots.push(isoAtMinutes(date, m))
    }
    return slots
}

export function hoursFromSlotIsos(slots: string[]): number[] {
    const hours = new Set<number>()
    for (const iso of slots) {
        hours.add(parseSlotIso(iso).hour)
    }
    return Array.from(hours).sort((a, b) => a - b)
}

export function minutesForHourFromSlots(slots: string[], hour: number): number[] {
    return slots
        .filter(iso => parseSlotIso(iso).hour === hour)
        .map(iso => parseSlotIso(iso).minute)
        .sort((a, b) => a - b)
}

export function nearestSlotInList(iso: string, slots: string[]): string {
    if (slots.length === 0) return iso
    if (slots.includes(iso)) return iso
    const target = minutesOfDay(iso)
    return slots.reduce((best, candidate) => {
        const bestDelta = Math.abs(minutesOfDay(best) - target)
        const candidateDelta = Math.abs(minutesOfDay(candidate) - target)
        return candidateDelta < bestDelta ? candidate : best
    })
}

export function reconcileEndForStart(
    date: string,
    clubOpen: string,
    clubClose: string,
    newStart: string,
    currentEnd: string
): string {
    const endSlots = buildEndSlotIsos(date, clubOpen, clubClose, newStart)
    if (endSlots.length === 0) {
        return addMinutesToIso(newStart, SLOT_MINUTES)
    }
    const duration = minutesBetween(newStart, currentEnd)
    const preferred = addMinutesToIso(newStart, Math.max(SLOT_MINUTES, duration))
    if (endSlots.includes(preferred)) {
        return preferred
    }
    return endSlots[0]
}

export function indexOfSlotInList(iso: string, slots: string[]): number {
    const exact = slots.indexOf(iso)
    if (exact >= 0) return exact
    const resolved = nearestSlotInList(iso, slots)
    return slots.indexOf(resolved)
}

/** Step by one 15-min slot in the ordered list (handles minute rollover across hours). */
export function stepSlotInList(slots: string[], iso: string, delta: number): string | null {
    if (slots.length === 0) return null
    const idx = indexOfSlotInList(iso, slots)
    if (idx < 0) return null
    const next = idx + delta
    if (next < 0 || next >= slots.length) return null
    return slots[next]
}
